import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router";
import { motion, AnimatePresence } from "motion/react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { BOTTOM_DOCK_INSET_PROPERTY } from "./editorial/bottomDockInset";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ---------------------------------------------------------------------------
// Consent helpers — localStorage for persistence
// ---------------------------------------------------------------------------

const CONSENT_KEY = "fairpath_cookie_consent";
const CONSENT_DATE_KEY = "fairpath_cookie_consent_date";
export const COOKIE_CONSENT_EVENT = "fairpath:cookie-consent-changed";

type ConsentValue = "accepted" | "declined";

/** Read the consent value, or null if absent. */
export function getCookieConsent(): ConsentValue | null {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    if (value === "accepted" || value === "declined") return value;
    return null;
  } catch {
    return null;
  }
}

/** Write consent choice + timestamp to localStorage. */
function setCookieConsent(value: ConsentValue) {
  try {
    localStorage.setItem(CONSENT_KEY, value);
    localStorage.setItem(CONSENT_DATE_KEY, new Date().toISOString());
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT, { detail: value }));
  } catch {
    // localStorage unavailable — banner will reappear next visit
  }
}

// ---------------------------------------------------------------------------
// Path exclusions — never interrupt conversion-critical pages
// ---------------------------------------------------------------------------

const CONSENT_EXCLUDED_PATHS = ["/checkout", "/a/"];

export { BOTTOM_DOCK_INSET_PROPERTY };

function isExcludedPath(pathname: string): boolean {
  return CONSENT_EXCLUDED_PATHS.some((path) => pathname.startsWith(path));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CookieConsentBannerProps {
  onNavigate: (view: string) => void;
}

export const CookieConsentBanner = ({
  onNavigate,
}: CookieConsentBannerProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  // PostHog is not initialized before consent, so no opt-out needed on mount.
  // If user previously declined, PostHog was never init'd — nothing to do.

  useEffect(() => {
    // Already consented or on an excluded page — don't show
    if (getCookieConsent() || isExcludedPath(location.pathname)) return;

    // 1.5s delay — let the page load and user orient first
    const timer = setTimeout(() => setIsVisible(true), 1500);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // While the bar is shown it publishes its height, so anything docked to the
  // bottom edge (the /check next-step bar, FAI-97) can sit clear of it.
  const barRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const root = document.documentElement;
    const bar = barRef.current;
    if (!isVisible || !bar) {
      root.style.removeProperty(BOTTOM_DOCK_INSET_PROPERTY);
      return;
    }
    const publish = () => root.style.setProperty(BOTTOM_DOCK_INSET_PROPERTY, `${bar.offsetHeight}px`);
    publish();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publish);
    observer?.observe(bar);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(BOTTOM_DOCK_INSET_PROPERTY);
    };
  }, [isVisible]);

  const dismiss = useCallback(() => {
    setIsVisible(false);
  }, []);

  const handleAccept = () => {
    setCookieConsent("accepted");
    dismiss();

    import("../../lib/posthog").then(({ initPostHog, upgradePostHogPersistence }) => {
      initPostHog();
      upgradePostHogPersistence();
    });
    import("../../lib/fbpixel").then(({ initFacebookPixel }) => {
      initFacebookPixel();
    });
    import("../../lib/gtag").then(({ initGoogleAds }) => {
      initGoogleAds();
    });
    import("../../lib/openaiAds").then(({ initOpenAIAdsPixel }) => {
      initOpenAIAdsPixel();
    });
  };

  const handleDecline = () => {
    setCookieConsent("declined");
    dismiss();
    // PostHog was never initialized (consent-gated), so nothing to opt out of.
    // Sentry continues — error monitoring is legitimate interest under CCPA.
  };

  const handlePrivacyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onNavigate("privacy");
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          ref={barRef}
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          role="region"
          aria-label="Cookie consent"
          className={cn(
            // A full-width bar pinned to the bottom edge. The previous floating
            // card sat on top of the hero on mobile and over section headings
            // on desktop; a bar only ever covers the viewport's bottom strip.
            "fixed inset-x-0 bottom-0 z-40",
            "border-t border-fp-slate-300 bg-fp-slate-100/95 text-fp-navy-900 backdrop-blur-sm",
            "px-4 py-3 sm:px-6",
          )}
        >
          <div className="mx-auto flex max-w-6xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          {/* Text */}
          <p className="min-w-0 flex-1 text-[14px] leading-snug text-fp-slate-700 sm:text-[14px]">
            FairPath uses{" "}
            <button
              onClick={handlePrivacyClick}
              // theme.css gives every <button> the base font size and medium
              // weight; an inline button must opt back into its sentence.
              className="inline cursor-pointer border-none bg-transparent p-0 text-[14px] font-normal leading-snug underline decoration-fp-slate-300 underline-offset-2 hover:decoration-fp-emerald-500 sm:text-[14px]"
            >
              cookies
            </button>{" "}
            for optional analytics. Error monitoring does not use advertising cookies.
          </p>

          {/* Buttons */}
          <div className="flex shrink-0 items-center justify-end gap-2">
            <button
              onClick={handleDecline}
              className={cn(
                "text-fp-slate-500 text-sm sm:text-[15px] px-2 py-1.5",
                "hover:text-fp-navy-900 transition-colors",
                "cursor-pointer bg-transparent border-none",
              )}
            >
              Decline
            </button>

            <button
              onClick={handleAccept}
              className={cn(
                "bg-fp-navy-900 text-white text-sm sm:text-[15px] font-semibold",
                "px-3 py-1.5 rounded-fp",
                "hover:bg-fp-emerald-600 transition-colors",
                "cursor-pointer",
              )}
            >
              Accept
            </button>
          </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
